import type { CarePlan } from '@medplum/fhirtypes';
import { AnimatePresence, motion } from 'framer-motion';

import { Button, Icon, LoadingIcon, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { PLAN_SHAPES } from '@eluve/plan-shapes';

import { carePlanSummary } from './care-plan-meta';
import {
  type CarePlanDraftState,
  isCarePlanDraftGenerating,
} from './useAppointmentCarePlanDraft';

const WRAPPER_CLASS =
  'flex w-full flex-wrap items-center gap-3 rounded-xl border px-4 py-3.5 shadow-sm';

const Wrapper: React.FC<{
  className: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={`${WRAPPER_CLASS} ${className}`}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

const DismissButton: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={t('carePlans.callout.dismiss')}
      onClick={onDismiss}
    >
      <Icon name="X" size="auto" />
    </Button>
  );
};

export function carePlanProgressMessage(
  state: CarePlanDraftState,
): string | null {
  switch (state) {
    case 'generating-note':
      return 'Generating the visit note';
    case 'extracting-findings':
      return 'Extracting clinical findings';
    case 'generating-care-plan':
      return 'Generating your Care Plan';
    default:
      return null;
  }
}

// The animated banner above the active tab content tracks the end-of-encounter
// pipeline from note generation through a ready Care Plan. Dismiss is
// session-local (the parent drops it from state).
export const CarePlanCallout: React.FC<{
  state: CarePlanDraftState;
  carePlan: CarePlan | null;
  onReview: () => void;
  onDismiss: () => void;
}> = ({ state, carePlan, onReview, onDismiss }) => {
  const { t } = useTranslation();
  if (state === 'ready' && carePlan) {
    const { title, shape, phaseCount, weeks, matchPct } =
      carePlanSummary(carePlan);
    // Phase count/weeks only describe a phased plan; for verbatim
    // (continuous/episodic) drafts show just the shape label (ENG-4773).
    const isPhased = !shape || shape === 'phased';
    const meta = [
      shape ? PLAN_SHAPES[shape].label : t('carePlans.tab.phased'),
      isPhased
        ? t(
            phaseCount === 1
              ? 'carePlans.counts.phaseOne'
              : 'carePlans.counts.phaseMany',
            { count: phaseCount },
          )
        : null,
      isPhased && weeks
        ? t('carePlans.tab.weeksCount', { count: weeks })
        : null,
      matchPct !== null
        ? t('carePlans.tab.percentMatch', { pct: matchPct })
        : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Wrapper className="border-border-indigo-subtle bg-linear-to-r from-bg-indigo-subtle-secondary to-transparent">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-indigo-strong-primary text-text-indigo-strong">
          <Icon name="ClipboardCheck" size="auto" className="size-4" />
        </span>
        <Text size="sm" className="min-w-0 text-foreground">
          {t('carePlans.callout.draftedPrefix')}{' '}
          <span className="font-semibold">
            {t('carePlans.callout.carePlan')}
          </span>{' '}
          {t('carePlans.callout.draftedSuffix')}{' '}
          <span className="font-semibold">{title}</span>
          <span className="text-muted-foreground"> · {meta}</span>
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="brand" size="sm" onClick={onReview}>
            {t('carePlans.callout.reviewAndTailor')}
            <Icon name="ArrowRight" size="auto" />
          </Button>
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  if (isCarePlanDraftGenerating(state)) {
    const message = carePlanProgressMessage(state);
    return (
      <Wrapper className="border-border-indigo-subtle bg-linear-to-r from-bg-indigo-subtle-secondary to-transparent">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-indigo-strong-primary text-text-indigo-strong">
          <LoadingIcon size="sm" />
        </span>
        <Text size="sm" className="min-w-0 text-foreground">
          {message}
        </Text>
      </Wrapper>
    );
  }

  if (state === 'none') {
    return (
      <Wrapper className="border-border-primary bg-bg-secondary">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-bg-tertiary text-text-tertiary">
          <Icon name="Info" size="auto" className="size-4" />
        </span>
        <Text size="sm" color="secondary" className="min-w-0">
          {t('carePlans.callout.none')}
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  if (state === 'failed') {
    return (
      <Wrapper className="border-border-warning-subtle bg-bg-warning-subtle">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg text-text-warning">
          <Icon name="AlertTriangle" size="auto" className="size-4" />
        </span>
        <Text size="sm" className="min-w-0 text-text-warning">
          {t('carePlans.callout.failed')}
        </Text>
        <div className="ml-auto flex items-center gap-1">
          <DismissButton onDismiss={onDismiss} />
        </div>
      </Wrapper>
    );
  }

  return null;
};
